import { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import { WebsiteGrid, SectionedWebsiteGrid, type WebsiteLink } from "./WebsiteGrid";
import { MapDetailModal } from "./MapDetailModal";
import type { TabType } from "../App";
import { mapsData } from "../data/maps";
import { 
  STANDARD_BADGE_HEIGHT,
  categories,
  continents,
} from "../utils/constants";
import {
  matchesFilters,
  getAggregatedContinentCount,
  getAggregatedCountryCount,
  getAggregatedCityCount,
  getWorldMapsCount,
  getMapSections,
  shuffleSectionMaps,
  type MapSection,
} from "../utils/filterHelpers";
import {
  getLocationHierarchy,
  getAvailableContinents,
  getAvailableCountries,
  getAvailableCities,
  calculateLocationTabViewLevel,
} from "../utils/locationHelpers";
import { LocationTab } from "./tabs/LocationTab";
import { CategoriesTab } from "./tabs/CategoriesTab";
import { SearchTab } from "./tabs/SearchTab";
import { AboutTab } from "./tabs/AboutTab";
import { FilterBadges } from "./FilterBadges";
import { ScrollToTopButton } from "./ScrollToTopButton";

const websiteLinks: WebsiteLink[] = mapsData;

interface MainContentProps {
  activeTab: TabType;
  switchToMapTab: () => void;
  selectedMap: WebsiteLink | null;
  setSelectedMap: (map: WebsiteLink | null) => void;
  setHasLocationFilters: (hasFilters: boolean) => void;
  setHasCategoryFilters: (hasFilters: boolean) => void;
  setHasSearchFilters: (hasFilters: boolean) => void;
}

export function MainContent({
  activeTab,
  switchToMapTab,
  selectedMap,
  setSelectedMap,
  setHasLocationFilters,
  setHasCategoryFilters,
  setHasSearchFilters,
}: MainContentProps) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedContinents, setSelectedContinents] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedWorldMaps, setSelectedWorldMaps] = useState(false);
  const [selectedSearchTags, setSelectedSearchTags] = useState<string[]>([]);
  
  // Derived state: activeSectionType is calculated from filters (eliminates flash on tab switch)
  const activeSectionType = useMemo(() => {
    if (selectedCities && selectedCities.length > 0) return 'city';
    if (selectedCountries && selectedCountries.length > 0) return 'country';
    if (selectedContinents && selectedContinents.length > 0) return 'continent';
    if (selectedWorldMaps) return 'world';
    return null;
  }, [selectedCities, selectedCountries, selectedContinents, selectedWorldMaps]);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [locationTabViewLevel, setLocationTabViewLevel] = useState<'continents' | 'countries' | 'cities'>('continents');
  const [isWhyOpen, setIsWhyOpen] = useState(false);
  const [isHowOpen, setIsHowOpen] = useState(false);
  const [isWhatOpen, setIsWhatOpen] = useState(false);
  const [isAccountabilityOpen, setIsAccountabilityOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isDelistedOpen, setIsDelistedOpen] = useState(false);
  const [isCommercialRankingsOpen, setIsCommercialRankingsOpen] = useState(false);
  const [isMapsDirectoriesOpen, setIsMapsDirectoriesOpen] = useState(false);
  const [isTheBeginningOpen, setIsTheBeginningOpen] = useState(false);
  const [isDiscoveringMapsOpen, setIsDiscoveringMapsOpen] = useState(false);
  const mapsScrollContainerRef = useRef<HTMLDivElement>(null);
  const otherTabsScrollContainerRef = useRef<HTMLDivElement>(null);

  const prevTabRef = useRef<TabType>(activeTab);
  
  const filterStateWhenLeftMapsRef = useRef<string>('');

  const hasDeselectedInSession = useRef(false);
  
  const hasDeselectedTagInSession = useRef(false);
  
  const prevActiveSectionTypeRef = useRef<'world' | 'city' | 'country' | 'continent' | null>(null);
  
  const badgeContainerRef = useRef<HTMLDivElement>(null);
  const [badgeHeight, setBadgeHeight] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [animateBadge, setAnimateBadge] = useState(false);
  const shouldBounceOnNextContinent = useRef(false);
  const [filterTabHasMoreContent, setFilterTabHasMoreContent] = useState(true);
  const lastShuffleRef = useRef<{
    applyFiltersRef: typeof applyFiltersSectioned;
    sections: MapSection[];
  } | null>(null);

  useLayoutEffect(() => {
    if (activeTab === "search" && prevTabRef.current !== "search") {
      otherTabsScrollContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [activeTab]);

  // Reset mask state when switching tabs
  useLayoutEffect(() => {
    const isLeavingFilterTab = 
      (prevTabRef.current === 'location' || prevTabRef.current === 'categories' || prevTabRef.current === 'search') &&
      (activeTab === 'maps');
    
    const isEnteringLocationOrCategory = 
      (activeTab === 'location' || activeTab === 'categories') &&
      prevTabRef.current !== activeTab;
    
    const isEnteringSearch = 
      activeTab === 'search' &&
      prevTabRef.current !== activeTab;

    // Leaving filter tabs → hide mask
    if (isLeavingFilterTab) {
      setFilterTabHasMoreContent(false);
    }
    
    // Entering Location or Category → reset scroll THEN check scrollability IMMEDIATELY
    if (isEnteringLocationOrCategory) {
      const container = otherTabsScrollContainerRef.current;
      if (container) {
        container.scrollTop = 0; // Reset scroll position to top
        const hasScrollableContent = container.scrollHeight > container.clientHeight;
        const isScrolledToBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 1;
        setFilterTabHasMoreContent(hasScrollableContent && !isScrolledToBottom);
      } else {
        setFilterTabHasMoreContent(false);
      }
    }

    // Entering Search → start with mask visible, let debounced check hide it if at bottom
    if (isEnteringSearch) {
      setFilterTabHasMoreContent(true);
    }
  }, [activeTab]);
  
  
  // Reset Maps scroll position when entering Maps tab if filters changed while away
  useLayoutEffect(() => {
    const currentFilterState = JSON.stringify({
      categories: selectedCategories,
      tags: selectedSearchTags,
      continents: selectedContinents,
      countries: selectedCountries,
      cities: selectedCities,
      worldMaps: selectedWorldMaps
    });
    
    // When entering Maps tab from another tab
    if (activeTab === "maps" && prevTabRef.current !== "maps") {
      // If filters changed while we were away, scroll to top
      if (filterStateWhenLeftMapsRef.current && 
          currentFilterState !== filterStateWhenLeftMapsRef.current) {
        mapsScrollContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
      }
      // Update snapshot to current state
      filterStateWhenLeftMapsRef.current = currentFilterState;
    }
    
    // When leaving Maps tab, snapshot the current filter state
    if (prevTabRef.current === "maps" && activeTab !== "maps") {
      filterStateWhenLeftMapsRef.current = currentFilterState;
    }
  }, [activeTab, selectedCategories, selectedSearchTags, selectedContinents, selectedCountries, selectedCities, selectedWorldMaps]);

  // useLayoutEffect (not useEffect) prevents flash when entering Location tab with stale locationTabViewLevel.
  // This ensures state is correct BEFORE browser paint, eliminating the visual glitch where the wrong level
  // briefly appears (e.g., Berlin button flashing when filters are active). Safe because: no DOM reads,
  // fast filtering, single dependency [activeTab].
  useLayoutEffect(() => {
    const previousTab = prevTabRef.current;
    
    if (activeTab === "location" && previousTab !== "location") {
      const hasNoLocationBadges = selectedContinents.length === 0 && 
                                  selectedCountries.length === 0 && 
                                  selectedCities.length === 0 &&
                                  !selectedWorldMaps;
      
      if (hasNoLocationBadges) {
        shouldBounceOnNextContinent.current = true;
      }
      
      setAnimateBadge(false);
      
      const newLevel = calculateLocationTabViewLevel(
        websiteLinks,
        selectedContinents,
        selectedCountries,
        selectedCities,
        selectedCategories,
        selectedSearchTags
      );
      setLocationTabViewLevel(newLevel);
    }
    
    prevTabRef.current = activeTab;
  }, [activeTab]); // Removed filter dependencies - effect only runs when entering location tab, reads current filter state
  
  useEffect(() => {
    if (activeTab !== "location") {
      setAnimateBadge(false);
      shouldBounceOnNextContinent.current = false;
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "categories") {
      hasDeselectedInSession.current = false;
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "search") {
      hasDeselectedTagInSession.current = false;
    }
  }, [activeTab]);

  useEffect(() => {
    // Only update ref and scroll when ON Maps tab
    // This allows detection of changes that happened while on other tabs
    if (activeTab === "maps") {
      const previousSectionType = prevActiveSectionTypeRef.current;
      
      if (activeSectionType && previousSectionType !== activeSectionType) {
        mapsScrollContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
      }
      
      prevActiveSectionTypeRef.current = activeSectionType;
    }
  }, [activeSectionType, activeTab]);

  function hasNoFiltersActive() {
    return (
      selectedCountries.length === 0 &&
      selectedCategories.length === 0 &&
      selectedContinents.length === 0 &&
      selectedCities.length === 0 &&
      !selectedWorldMaps &&
      selectedSearchTags.length === 0
    );
  }

  function hasNoLocationFiltersActive() {
    return (
      selectedCountries.length === 0 &&
      selectedContinents.length === 0 &&
      selectedCities.length === 0 &&
      !selectedWorldMaps
    );
  }

  const resetScrollIfOnMaps = useCallback(() => {
    if (activeTab === "maps") {
      mapsScrollContainerRef.current?.scrollTo(0, 0);
    }
  }, [activeTab]);

  const removeContinentBadge = useCallback((continent: string) => {
    setSelectedContinents([]);
    setSelectedCountries([]);
    setSelectedCities([]);
    setAnimateBadge(false);
    
    // N.mechanic: if category/tag filters active, calculate proper drill-up level
    const hasFilters = selectedCategories.length > 0 || selectedSearchTags.length > 0;
    if (hasFilters) {
      const newLevel = calculateLocationTabViewLevel(
        websiteLinks,
        [],
        [],
        [],
        selectedCategories,
        selectedSearchTags
      );
      setLocationTabViewLevel(newLevel);
    } else {
      setLocationTabViewLevel('continents');
    }
    
    resetScrollIfOnMaps();
  }, [selectedCategories, selectedSearchTags, resetScrollIfOnMaps]);

  const removeCountryBadge = useCallback((country: string) => {
    setSelectedCountries([]);
    setSelectedCities([]);
    setAnimateBadge(false);
    
    // Find parent continent
    const mapWithCountry = websiteLinks.find(link => link.countries?.includes(country));
    const continent = mapWithCountry?.continent;
    
    if (continent) {
      // N.mechanic: if category/tag filters active, calculate proper drill-up level
      const hasFilters = selectedCategories.length > 0 || selectedSearchTags.length > 0;
      if (hasFilters) {
        const newLevel = calculateLocationTabViewLevel(
          websiteLinks,
          [continent], // continent after removal
          [], // no countries after removal
          [], // no cities after removal
          selectedCategories,
          selectedSearchTags
        );
        
        // HIERARCHY END OVERRIDE: After removing country, always drill up to show parent continent selected
        // This prevents showing countries/cities level with no selection (broken state)
        const finalLevel = (newLevel === 'countries' || newLevel === 'cities') ? 'continents' : newLevel;
        
        if (finalLevel === 'continents') {
          const continentCount = getAggregatedContinentCount(websiteLinks, continent, selectedCategories, selectedSearchTags);
          if (continentCount > 0) {
            setSelectedContinents([continent]);
          } else {
            setSelectedContinents([]);
          }
        }
        
        setLocationTabViewLevel(finalLevel);
      } else {
        setSelectedContinents([continent]);
        const isButtonVisible = locationTabViewLevel === 'countries' && 
                                selectedCountries.includes(country) &&
                                selectedCities.length === 0;
        
        if (isButtonVisible) {
          setLocationTabViewLevel('continents');
        } else {
          setLocationTabViewLevel('countries');
        }
      }
    } else {
      setLocationTabViewLevel('continents');
    }
    
    resetScrollIfOnMaps();
  }, [locationTabViewLevel, selectedCountries, selectedCities, selectedCategories, selectedSearchTags, resetScrollIfOnMaps]);

  const removeCityBadge = useCallback((city: string) => {
    setSelectedCities([]);
    setAnimateBadge(false);
    
    // Find parent country
    const mapWithCity = websiteLinks.find(link => link.cities?.includes(city));
    const country = mapWithCity?.countries?.[0];
    
    if (country) {
      // N.mechanic: if category/tag filters active, calculate proper drill-up level
      const hasFilters = selectedCategories.length > 0 || selectedSearchTags.length > 0;
      if (hasFilters) {
        // Find parent continent for the country
        const mapWithCountry = websiteLinks.find(link => link.countries?.includes(country));
        const continent = mapWithCountry?.continent;
        
        const newLevel = calculateLocationTabViewLevel(
          websiteLinks,
          continent ? [continent] : [], // continent if found
          [country], // country after removal
          [], // no cities after removal
          selectedCategories,
          selectedSearchTags
        );
        
        // HIERARCHY END OVERRIDE: After removing city, always drill up to show parent country selected
        // This prevents showing cities level with no city selected (broken state)
        const finalLevel = newLevel === 'cities' ? 'countries' : newLevel;
        
        if (finalLevel === 'continents') {
          setSelectedCountries([]);
          setSelectedContinents([]);
        } else if (finalLevel === 'countries') {
          setSelectedCountries([country]);
          setSelectedContinents(continent ? [continent] : []);
        }
        
        setLocationTabViewLevel(finalLevel);
      } else {
        setSelectedCountries([country]);
        setLocationTabViewLevel('countries');
      }
    } else {
      setLocationTabViewLevel('continents');
    }
    
    resetScrollIfOnMaps();
  }, [selectedCategories, selectedSearchTags, resetScrollIfOnMaps]);

  const removeWorldMapsBadge = useCallback(() => {
    setSelectedWorldMaps(false);
    setAnimateBadge(false);
    setLocationTabViewLevel('continents');
    resetScrollIfOnMaps();
  }, [resetScrollIfOnMaps]);

  const toggleLocationBadge = useCallback((value: string, type: 'continent' | 'country' | 'city' | 'world') => {
    // Clear search query when location is selected from LocationTab (but keep tags)
    setSearchQuery("");
    
    if (type === 'world') {
      if (selectedWorldMaps) {
        return;
      }
      
      setSelectedWorldMaps(true);
      setSelectedContinents([]);
      setSelectedCountries([]);
      setSelectedCities([]);
      setAnimateBadge(false);
      switchToMapTab();
      return;
    }

    if (type === 'continent') {
      const isAlreadySelected = selectedContinents.includes(value) && 
                                selectedCountries.length === 0 && 
                                selectedCities.length === 0;
      
      if (isAlreadySelected) {
        const availableCountries = getAvailableCountries(websiteLinks, value, selectedCategories, selectedSearchTags, matchesFilters);
        const hasCountries = availableCountries.length > 0;
        
        if (hasCountries) {
          setLocationTabViewLevel('countries');
        }
        return;
      }
      
      // Select continent badge (never toggle off - only badge X can remove)
      setSelectedContinents([value]);
      setSelectedCountries([]);
      setSelectedCities([]);
      setSelectedWorldMaps(false);
      
      const availableCountries = getAvailableCountries(websiteLinks, value, selectedCategories, selectedSearchTags, matchesFilters);
      const hasCountries = availableCountries.length > 0;
      
      if (hasCountries) {
        // AUTO-JUMP THRESHOLD: If continent + all lower levels has ≤5 maps, skip drill-down
        const continentCount = getAggregatedContinentCount(websiteLinks, value, selectedCategories, selectedSearchTags);
        if (continentCount <= 5) {
          switchToMapTab();
          return;
        }
        setLocationTabViewLevel('countries');
      } else {
        setLocationTabViewLevel('continents');
      }
      
      // Trigger bounce animation if this is the first continent selection after entering location tab
      if (shouldBounceOnNextContinent.current) {
        setAnimateBadge(true);
        shouldBounceOnNextContinent.current = false;
      }
      
      return;
    }

    if (type === 'country') {
      const isAlreadySelected = selectedCountries.includes(value) && 
                                selectedCities.length === 0;
      
      const availableCities = getAvailableCities(websiteLinks, value, selectedCategories, selectedSearchTags, matchesFilters);
      const hasCities = availableCities.length > 0;
      
      if (isAlreadySelected) {
        if (hasCities) {
          setLocationTabViewLevel('cities');
        }
        return;
      }
      
      setSelectedCountries([value]);
      setSelectedContinents([]);
      setSelectedCities([]);
      setSelectedWorldMaps(false);
      setAnimateBadge(false);
      
      // If no cities, it's the final level - close menu
      if (!hasCities) {
        switchToMapTab();
      } else {
        // AUTO-JUMP THRESHOLD: If country + all lower levels has ≤5 maps, skip drill-down
        const countryCount = getAggregatedCountryCount(websiteLinks, value, selectedCategories, selectedSearchTags);
        if (countryCount <= 5) {
          switchToMapTab();
          return;
        }
        setLocationTabViewLevel('cities');
      }
      return;
    }

    if (type === 'city') {
      if (selectedCities.includes(value)) {
        return;
      }
      
      setSelectedCities([value]);
      setSelectedContinents([]);
      setSelectedCountries([]);
      setSelectedWorldMaps(false);
      setAnimateBadge(false);
      // City is always final level, close menu
      switchToMapTab();
      return;
    }
  }, [switchToMapTab, selectedCategories, selectedSearchTags, selectedContinents, selectedCountries, selectedCities, locationTabViewLevel, selectedWorldMaps]);

  const derivedViewContinent = useMemo(() => {
    if (selectedCities.length > 0) {
      const city = selectedCities[0];
      const mapWithCity = websiteLinks.find(link => link.cities?.includes(city));
      return mapWithCity?.continent || null;
    }
    
    if (selectedCountries.length > 0) {
      const country = selectedCountries[0];
      const mapWithCountry = websiteLinks.find(link => link.countries?.includes(country));
      return mapWithCountry?.continent || null;
    }
    
    if (selectedContinents.length > 0) {
      return selectedContinents[0];
    }
    
    return null;
  }, [selectedContinents, selectedCountries, selectedCities]);

  const derivedViewCountry = useMemo(() => {
    if (selectedCities.length > 0) {
      const city = selectedCities[0];
      const mapWithCity = websiteLinks.find(link => link.cities?.includes(city));
      return mapWithCity?.countries?.[0] || null;
    }
    
    if (selectedCountries.length > 0) {
      return selectedCountries[0];
    }
    
    return null;
  }, [selectedCountries, selectedCities]);

  const getSelectedSearchLocation = useCallback((): string => {
    if (selectedWorldMaps) return "World";
    if (selectedContinents.length > 0) return selectedContinents[0];
    if (selectedCountries.length > 0) return selectedCountries[0];
    if (selectedCities.length > 0) return selectedCities[0];
    return "";
  }, [selectedWorldMaps, selectedContinents, selectedCountries, selectedCities]);

  const getFilteredCategories = useCallback(() => {
    const activeLocationFilters = [
      ...selectedContinents,
      ...selectedCountries,
      ...selectedCities,
    ];

    if (activeLocationFilters.length === 0 && selectedSearchTags.length === 0) {
      return [...categories].map((cat) => ({
        name: cat.displayName,
        count: 0,
      })).sort((a, b) => {
        if (a.name === "Last Added") return 1;
        if (b.name === "Last Added") return -1;
        return a.name.localeCompare(b.name);
      });
    }

    // Use hierarchical sections to get maps from selected location + all parent levels
    // This ensures we show categories from Heidelberg + Germany + Europe + World
    const sections = getMapSections(
      websiteLinks,
      [], // No category filter - we want all available categories
      selectedContinents,
      selectedCountries,
      selectedCities,
      selectedWorldMaps,
      selectedSearchTags
    );

    // Collect all maps from all sections (includes parent levels)
    const filteredMaps = sections.flatMap(s => s.maps);

    const categoryCounts = new Map<string, number>();
    
    categories.forEach((catButton) => {
      const count = filteredMaps.filter((map) =>
        catButton.categories.some((cat) => map.categories.includes(cat))
      ).length;
      categoryCounts.set(catButton.displayName, count);
    });

    return categories
      .map((cat) => ({
        name: cat.displayName,
        count: categoryCounts.get(cat.displayName) || 0,
      }))
      .filter((cat) => cat.count > 0 || selectedCategories.includes(cat.name))
      .sort((a, b) => {
        if (a.name === "Last Added") return 1;
        if (b.name === "Last Added") return -1;
        return a.name.localeCompare(b.name);
      });
  }, [selectedContinents, selectedCountries, selectedCities, selectedSearchTags, selectedCategories, selectedWorldMaps]);

  const filteredCategories = useMemo(() => {
    if (activeTab !== "categories") return [];
    
    return getFilteredCategories();
  }, [activeTab, getFilteredCategories]);
  
  const applyFiltersSectioned = useCallback((): MapSection[] => {
    // Always use sectioned filtering to show all location sections
    const sections = getMapSections(
      websiteLinks,
      selectedCategories,
      selectedContinents,
      selectedCountries,
      selectedCities,
      selectedWorldMaps,
      selectedSearchTags
    );
    
    return sections;
  }, [selectedCategories, selectedContinents, selectedCountries, selectedCities, selectedWorldMaps, selectedSearchTags]);

  const filteredSections = useMemo(() => {
    if (activeTab !== "maps") {
      return [];
    }
    
    const applyFiltersRef = applyFiltersSectioned;
    
    if (lastShuffleRef.current && lastShuffleRef.current.applyFiltersRef === applyFiltersRef) {
      return lastShuffleRef.current.sections;
    }
    
    const sections = applyFiltersRef();
    const shuffledSections = shuffleSectionMaps(sections);
    lastShuffleRef.current = { applyFiltersRef, sections: shuffledSections };
    return shuffledSections;
  }, [activeTab, applyFiltersSectioned]);
  
  // PERFORMANCE OPTIMIZATION: Derive count from filteredSections when on Maps tab
  // to eliminate duplicate getMapSections() computation. On other tabs, compute
  // independently since filteredSections only exists on Maps tab (filteredSections
  // returns [] when activeTab !== "maps").
  const filteredLinksCount = useMemo(() => {
    // On Maps tab: derive from already-computed filteredSections (avoid duplicate filtering)
    if (activeTab === "maps" && filteredSections.length > 0) {
      return filteredSections.reduce((total, section) => total + section.maps.length, 0);
    }
    
    // On other tabs: compute independently (FilterBadges needs count on all tabs)
    const sections = getMapSections(
      websiteLinks,
      selectedCategories,
      selectedContinents,
      selectedCountries,
      selectedCities,
      selectedWorldMaps,
      selectedSearchTags
    );
    
    return sections.reduce((total, section) => total + section.maps.length, 0);
  }, [activeTab, filteredSections, selectedCategories, selectedContinents, selectedCountries, selectedCities, selectedWorldMaps, selectedSearchTags]);

  const effectiveActiveSectionType = useMemo(() => {
    // Not needed when not on Maps tab
    if (activeTab !== "maps") {
      return null;
    }
    
    // If location badges exist, use the derived activeSectionType from badges
    if (activeSectionType !== null) {
      return activeSectionType;
    }
    
    // If no location badges but category/tag filters exist, auto-select first expandable section with content
    const hasNoLocationBadges = !selectedWorldMaps && 
                                selectedContinents.length === 0 && 
                                selectedCountries.length === 0 && 
                                selectedCities.length === 0;
    const hasCategoryOrTagFilters = selectedCategories.length > 0 || selectedSearchTags.length > 0;
    
    if (hasNoLocationBadges && hasCategoryOrTagFilters) {
      // Find first expandable section (has label) with content in priority order
      const firstExpandable = filteredSections.find(s => s.label && s.maps.length > 0);
      return firstExpandable?.type || null;
    }
    
    return null;
  }, [activeTab, activeSectionType, filteredSections, selectedWorldMaps, selectedContinents, selectedCountries, selectedCities, selectedCategories, selectedSearchTags]);

  const memoizedGetAvailableContinents = useCallback(() => {
    return getAvailableContinents(websiteLinks, selectedCategories, selectedSearchTags, matchesFilters);
  }, [selectedCategories, selectedSearchTags]);

  const memoizedGetAvailableCountries = useCallback((continent: string) => {
    return getAvailableCountries(websiteLinks, continent, selectedCategories, selectedSearchTags, matchesFilters);
  }, [selectedCategories, selectedSearchTags]);

  const memoizedGetAvailableCities = useCallback((country: string) => {
    return getAvailableCities(websiteLinks, country, selectedCategories, selectedSearchTags, matchesFilters);
  }, [selectedCategories, selectedSearchTags]);

  const memoizedGetAggregatedContinentCount = useCallback((continent: string) => {
    return getAggregatedContinentCount(websiteLinks, continent, selectedCategories, selectedSearchTags);
  }, [selectedCategories, selectedSearchTags]);

  const memoizedGetAggregatedCountryCount = useCallback((country: string) => {
    return getAggregatedCountryCount(websiteLinks, country, selectedCategories, selectedSearchTags);
  }, [selectedCategories, selectedSearchTags]);

  const memoizedGetAggregatedCityCount = useCallback((city: string) => {
    return getAggregatedCityCount(websiteLinks, city, selectedCategories, selectedSearchTags);
  }, [selectedCategories, selectedSearchTags]);

  const memoizedGetWorldMapsCount = useCallback(() => {
    return getWorldMapsCount(websiteLinks, selectedCategories, selectedSearchTags);
  }, [selectedCategories, selectedSearchTags]);

  const memoizedGetLocationHierarchy = useMemo(() => {
    return getLocationHierarchy(websiteLinks);
  }, []); // websiteLinks is a constant, no dependency needed

  const selectCategory = useCallback((category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(prev => prev.filter(cat => cat !== category));
      hasDeselectedInSession.current = true;
      return;
    }
    
    setSelectedCategories(prev => [...prev, category]);
    
    if (selectedCategories.length === 0 && !hasDeselectedInSession.current) {
      switchToMapTab();
    }
  }, [selectedCategories, switchToMapTab]);

  const selectSearchLocation = useCallback((location: string) => {
    const isWorld = location === "World";
    const isContinent = continents.includes(location);
    const hierarchy = memoizedGetLocationHierarchy;
    const isCountry = Object.values(hierarchy).some(continentData => 
      Object.keys(continentData).includes(location)
    );
    const isCity = Object.values(hierarchy).some(continentData => 
      Object.values(continentData).some(cities => cities.has(location))
    );
    
    const isAlreadySelected = 
      (isWorld && selectedWorldMaps) ||
      (isContinent && selectedContinents.includes(location)) ||
      (isCountry && selectedCountries.includes(location)) ||
      (isCity && selectedCities.includes(location));
    
    if (isAlreadySelected) {
      if (isWorld) setSelectedWorldMaps(false);
      else if (isContinent) setSelectedContinents([]);
      else if (isCountry) setSelectedCountries([]);
      else if (isCity) setSelectedCities([]);
    } else {
      setSelectedContinents([]);
      setSelectedCountries([]);
      setSelectedCities([]);
      setSelectedWorldMaps(false);
      
      if (isWorld) {
        setSelectedWorldMaps(true);
      } else if (isContinent) {
        setSelectedContinents([location]);
      } else if (isCountry) {
        setSelectedCountries([location]);
      } else if (isCity) {
        setSelectedCities([location]);
      }
      
      setSelectedCategories([]);
    }
    // Keep search query to allow multi-word searches
  }, [selectedWorldMaps, selectedContinents, selectedCountries, selectedCities, memoizedGetLocationHierarchy]);

  const selectSearchTag = useCallback((tag: string) => {
    if (selectedSearchTags.includes(tag)) {
      // Deselecting - NO auto-jump
      setSelectedSearchTags((prev) =>
        prev.filter((t) => t !== tag),
      );
      hasDeselectedTagInSession.current = true;
    } else {
      // Selecting - Smart auto-jump logic (like Categories)
      setSelectedSearchTags((prev) => [...prev, tag]);
      
      // Only auto-jump on FIRST selection + no prior deselection
      if (selectedSearchTags.length === 0 && !hasDeselectedTagInSession.current) {
        switchToMapTab();
      }
    }
    // Keep search query to allow multi-word searches
  }, [selectedSearchTags, switchToMapTab]);

  const clearCategoriesFromBadge = useCallback(() => {
    setSelectedCategories([]);
    hasDeselectedInSession.current = true; // Mark deselection to prevent auto-close
    // Auto-remove World if tags are also empty
    if (selectedWorldMaps && selectedSearchTags.length === 0) {
      setSelectedWorldMaps(false);
    }
    
    // Recalculate view level if on Location tab (fixes stale state bug)
    if (activeTab === 'location') {
      const newLevel = calculateLocationTabViewLevel(
        websiteLinks,
        selectedContinents,
        selectedCountries,
        selectedCities,
        [], // Categories now empty
        selectedSearchTags
      );
      setLocationTabViewLevel(newLevel);
    }
    
    resetScrollIfOnMaps();
  }, [activeTab, selectedWorldMaps, selectedSearchTags, selectedContinents, selectedCountries, selectedCities, resetScrollIfOnMaps]);

  const clearTagsFromBadge = useCallback(() => {
    setSelectedSearchTags([]);
    hasDeselectedTagInSession.current = true; // Mark deselection to prevent auto-close
    // Auto-remove World if categories are also empty
    if (selectedWorldMaps && selectedCategories.length === 0) {
      setSelectedWorldMaps(false);
    }
    
    // Recalculate view level if on Location tab (fixes stale state bug)
    if (activeTab === 'location') {
      const newLevel = calculateLocationTabViewLevel(
        websiteLinks,
        selectedContinents,
        selectedCountries,
        selectedCities,
        selectedCategories,
        [] // Tags now empty
      );
      setLocationTabViewLevel(newLevel);
    }
    
    resetScrollIfOnMaps();
  }, [activeTab, selectedWorldMaps, selectedCategories, selectedContinents, selectedCountries, selectedCities, resetScrollIfOnMaps]);

  const clearLocationFilters = useCallback(() => {
    setSelectedContinents([]);
    setSelectedCountries([]);
    setSelectedCities([]);
    setSelectedWorldMaps(false);
    setLocationTabViewLevel('continents');
    resetScrollIfOnMaps();
  }, [resetScrollIfOnMaps]);

  useEffect(() => {
    if (activeTab !== "about") {
      setIsWhyOpen(false);
      setIsHowOpen(false);
      setIsWhatOpen(false);
      setIsAccountabilityOpen(false);
      setIsContactOpen(false);
      setIsDelistedOpen(false);
      setIsCommercialRankingsOpen(false);
      setIsMapsDirectoriesOpen(false);
      setIsTheBeginningOpen(false);
      setIsDiscoveringMapsOpen(false);
    }
  }, [activeTab]);

  useLayoutEffect(() => {
    const measureBadgeHeight = () => {
      if (activeTab === "about") {
        setBadgeHeight(0);
      } else if (activeTab === "maps" || activeTab === "location" || activeTab === "categories" || activeTab === "search") {
        const height = badgeContainerRef.current?.offsetHeight || STANDARD_BADGE_HEIGHT;
        setBadgeHeight(height);
      }
      
      setIsDesktop(window.innerWidth >= 768);
    };

    // Deferred initial measurement (prevents forced reflow)
    const initialRafId = requestAnimationFrame(() => {
      measureBadgeHeight();
    });
    
    // Deferred measurement on resize (prevents forced reflow warning)
    let rafId: number | null = null;
    const handleResize = () => {
      if (rafId !== null) return; // Skip if already scheduled
      rafId = requestAnimationFrame(() => {
        measureBadgeHeight();
        rafId = null;
      });
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(initialRafId);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [activeTab]);

  useLayoutEffect(() => {
    if (activeTab !== "maps" && activeTab !== "location" && activeTab !== "categories" && activeTab !== "search") {
      return;
    }

    // Synchronous measurement (before paint) - prevents second render jitter
    if (badgeContainerRef.current) {
      const height = badgeContainerRef.current.offsetHeight;
      setBadgeHeight(height);
    }
  }, [activeTab, selectedCategories, selectedContinents, selectedCountries, selectedCities, selectedSearchTags, selectedWorldMaps]);

  // ============================================================================
  // FILTER TABS BOTTOM FADE INDICATOR
  // Shows content fade-out for Location, Categories, and Search tabs (mobile only)
  // Uses useLayoutEffect to prevent flash when entering these tabs
  // Updates on scroll to hide when reaching bottom
  // Separate from Maps scroll logic - uses otherTabsScrollContainerRef
  // Safe to delete without affecting other scroll/layout behavior
  // ============================================================================
  useLayoutEffect(() => {
    // Only active on Location, Categories, or Search tabs
    const isFilterTab = activeTab === 'location' || activeTab === 'categories' || activeTab === 'search';
    
    if (!isFilterTab) {
      
      return; // Don't reset state - keep mask ready for next filter tab 
    }
    
    const container = otherTabsScrollContainerRef.current;
    if (!container) return;
    
    let rafId: number | null = null;
    
    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
    
   const checkScrollableContent = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Only update state after scrolling stops (150ms of no scroll activity)
      scrollTimeout = setTimeout(() => {
        rafId = requestAnimationFrame(() => {
          const hasMore = container.scrollTop + container.clientHeight < container.scrollHeight - 15;
          setFilterTabHasMoreContent(hasMore);
          rafId = null;
        });
      }, 25);
    };
    
    checkScrollableContent();
    container.addEventListener('scroll', checkScrollableContent, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', checkScrollableContent);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [activeTab, selectedContinents, selectedCountries, selectedCities, selectedCategories, searchQuery]);
  
  useEffect(() => {
    const hasLocation = selectedContinents.length > 0 || selectedCountries.length > 0 || selectedCities.length > 0 || selectedWorldMaps;
    const hasCategory = selectedCategories.length > 0;
    const hasSearch = selectedSearchTags.length > 0 || hasLocation;
    setHasLocationFilters(hasLocation);
    setHasCategoryFilters(hasCategory);
    setHasSearchFilters(hasSearch);
  }, [selectedContinents, selectedCountries, selectedCities, selectedWorldMaps, selectedCategories, selectedSearchTags, setHasLocationFilters, setHasCategoryFilters, setHasSearchFilters]);

  // Calculate effective bottom value with synchronous DOM measurement when leaving About tab
  const effectiveBottomValue = (() => {
    if (activeTab === "about") {
      return `${isDesktop ? 77 : 67}px`;
    }
    
    // When leaving About tab, read directly from DOM during render to get correct height immediately
    // This bypasses the state update delay and ensures the frame is positioned correctly on first paint
    if (prevTabRef.current === "about") {
      const actualHeight = badgeContainerRef.current?.offsetHeight || STANDARD_BADGE_HEIGHT;
      return `${actualHeight + (isDesktop ? 77 : 67)}px`;
    }
    
    // Normal case: use state
    return `${badgeHeight + (isDesktop ? 77 : 67)}px`;
  })();

  // Disable transition when leaving About tab OR entering Maps tab OR entering About tab
  const shouldDisableTransition = 
    (prevTabRef.current === "about" && activeTab !== "about") ||  // Leaving About (original)
    (prevTabRef.current !== "maps" && activeTab === "maps") ||    // Entering Maps (NEW)
    (activeTab === "about" && prevTabRef.current !== "about");    // Entering About (eliminates gradual content shift)

  return (
    <main 
      id="main-content"
      className={`fixed inset-0 pt-3 flex flex-col mx-auto px-[1px] ${activeTab === "maps" ? "md:px-4 lg:px-6 max-w-[1700px]" : ""}`}
      style={{
        bottom: effectiveBottomValue,
        transition: shouldDisableTransition ? 'none' : 'bottom 50ms ease-out'
      }}
    >
      <h1 className="sr-only">Discover Maps - A Map Projects Directory</h1>
      
      <div 
        role="status" 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      >
        {activeTab === "maps" && "Maps view"}
        {activeTab === "location" && "Location filters view"}
        {activeTab === "categories" && "Categories filters view"}
        {activeTab === "search" && "Search view"}
        {activeTab === "about" && "About information view"}
      </div>
      
      <div 
        role="status" 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      >
        {activeTab === "maps" && !hasNoFiltersActive() && (
          `Showing ${filteredLinksCount} of ${mapsData.length} maps`
        )}
      </div>
      
      <div className={`bg-gradient-to-r from-[var(--gradient-light-start)] to-[var(--gradient-light-end)] rounded-[15px] border border-transparent relative flex-1 flex flex-col overflow-hidden`}>
        
        {/* Maps tab - independent scroll container (always mounted, visibility toggled) */}
        <div 
          ref={mapsScrollContainerRef}
          className={`px-[2px] md:px-4 lg:px-6 pt-1 flex-1 overflow-y-auto`}
          style={{ display: activeTab === "maps" ? "block" : "none" }}
        >
          <div className="flex flex-col h-full">
            <SectionedWebsiteGrid
              sections={filteredSections}
              onMapClick={setSelectedMap}
              showLocationInfo={!selectedWorldMaps}
              scrollContainer={mapsScrollContainerRef}
              selectedContinents={selectedContinents}
              selectedCountries={selectedCountries}
              selectedCities={selectedCities}
              selectedWorldMaps={selectedWorldMaps}
              selectedCategories={selectedCategories}
              selectedSearchTags={selectedSearchTags}
              activeSectionType={effectiveActiveSectionType}
            />
          </div>
        </div>

        {/* Other tabs - shared scroll container with bottom fade indicator */}
        <div 
          ref={otherTabsScrollContainerRef}
          className={`px-[2px] pt-1 flex-1 overflow-y-auto`}
          style={{ 
            display: activeTab !== "maps" ? "block" : "none",
            maskImage: (filterTabHasMoreContent && activeTab !== 'about' && activeTab !== 'maps')
              ? 'linear-gradient(to bottom, black calc(100% - 50px), rgba(0, 0, 0, 0.08) 100%)'
              : 'none',
            WebkitMaskImage: (filterTabHasMoreContent && activeTab !== 'about' && activeTab !== 'maps')
              ? 'linear-gradient(to bottom, black calc(100% - 50px), rgba(0, 0, 0, 0.08) 100%)'
              : 'none',
          }}
        >
          {/* Location Tab Content */}
          {activeTab === "location" && (
            <LocationTab
              viewContinent={derivedViewContinent}
              viewCountry={derivedViewCountry}
              selectedCategories={selectedCategories}
              selectedSearchTags={selectedSearchTags}
              selectedContinents={selectedContinents}
              selectedCountries={selectedCountries}
              selectedCities={selectedCities}
              selectedWorldMaps={selectedWorldMaps}
              getAvailableContinents={memoizedGetAvailableContinents}
              getAvailableCountries={memoizedGetAvailableCountries}
              getAvailableCities={memoizedGetAvailableCities}
              getAggregatedContinentCount={memoizedGetAggregatedContinentCount}
              getAggregatedCountryCount={memoizedGetAggregatedCountryCount}
              getAggregatedCityCount={memoizedGetAggregatedCityCount}
              getWorldMapsCount={memoizedGetWorldMapsCount}
              toggleLocationBadge={toggleLocationBadge}
              switchToMapTab={switchToMapTab}
              locationTabViewLevel={locationTabViewLevel}
            />
          )}

          {/* Categories Tab Content */}
          {activeTab === "categories" && (
            <CategoriesTab
              filteredCategories={filteredCategories}
              selectedCategories={selectedCategories}
              selectCategory={selectCategory}
            />
          )}

          {/* Search Tab Content */}
          {activeTab === "search" && (
            <SearchTab
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedSearchTags={selectedSearchTags}
              selectedCategories={selectedCategories}
              selectedContinents={selectedContinents}
              selectedCountries={selectedCountries}
              selectedCities={selectedCities}
              selectedWorldMaps={selectedWorldMaps}
              getSelectedSearchLocation={getSelectedSearchLocation}
              selectSearchTag={selectSearchTag}
              allMaps={websiteLinks}
              onMapClick={setSelectedMap}
            />
          )}

          {/* About Tab Content */}
          {activeTab === "about" && (
            <AboutTab
              isWhyOpen={isWhyOpen}
              setIsWhyOpen={setIsWhyOpen}
              isHowOpen={isHowOpen}
              setIsHowOpen={setIsHowOpen}
              isWhatOpen={isWhatOpen}
              setIsWhatOpen={setIsWhatOpen}
              isAccountabilityOpen={isAccountabilityOpen}
              setIsAccountabilityOpen={setIsAccountabilityOpen}
              isContactOpen={isContactOpen}
              setIsContactOpen={setIsContactOpen}
              isDelistedOpen={isDelistedOpen}
              setIsDelistedOpen={setIsDelistedOpen}
              isCommercialRankingsOpen={isCommercialRankingsOpen}
              setIsCommercialRankingsOpen={setIsCommercialRankingsOpen}
              isMapsDirectoriesOpen={isMapsDirectoriesOpen}
              setIsMapsDirectoriesOpen={setIsMapsDirectoriesOpen}
              isTheBeginningOpen={isTheBeginningOpen}
              setIsTheBeginningOpen={setIsTheBeginningOpen}
              isDiscoveringMapsOpen={isDiscoveringMapsOpen}
              setIsDiscoveringMapsOpen={setIsDiscoveringMapsOpen}
            />
          )}
        </div>
        
      </div>

      {selectedMap && (
        <MapDetailModal
          map={selectedMap}
          onClose={() => setSelectedMap(null)}
        />
      )}

      <FilterBadges
        activeTab={activeTab}
        badgeContainerRef={badgeContainerRef}
        hasNoFiltersActive={hasNoFiltersActive}
        hasNoLocationFiltersActive={hasNoLocationFiltersActive}
        selectedContinents={selectedContinents}
        selectedCountries={selectedCountries}
        selectedCities={selectedCities}
        selectedWorldMaps={selectedWorldMaps}
        selectedCategories={selectedCategories}
        selectedSearchTags={selectedSearchTags}
        filteredLinksCount={filteredLinksCount}
        clearLocationFilters={clearLocationFilters}
        removeContinentBadge={removeContinentBadge}
        removeCountryBadge={removeCountryBadge}
        removeCityBadge={removeCityBadge}
        removeWorldMapsBadge={removeWorldMapsBadge}
        clearCategoriesFromBadge={clearCategoriesFromBadge}
        clearTagsFromBadge={clearTagsFromBadge}
        animateBadge={animateBadge}
      />

      <ScrollToTopButton
        scrollContainerRef={mapsScrollContainerRef}
        activeTab={activeTab}
      />
    </main>
  );
}